import { useState } from "react";
import { Minus, Plus, PlusCircle, Truck } from "lucide-react";
import { wInput, wLabel, StepTitle, NavRow } from "./shared";

const INVENTORY_GROUPS = [
  { room: "Bedroom", items: ["Bed", "Mattress", "Bedside Table", "Wardrobe", "Chest of Drawers"] },
  { room: "Living Room", items: ["Sofa", "TV", "TV Unit", "Coffee Table", "Armchair", "Bookshelf"] },
  { room: "Dining", items: ["Dining Table", "Dining Chairs", "Buffet"] },
  { room: "Kitchen", items: ["Fridge", "Washing Machine", "Dishwasher", "Microwave"] },
  { room: "Other", items: ["Outdoor Furniture", "BBQ", "Piano", "Exercise Equipment", "Boxes"] },
];

export const Step6Inventory = ({ data, patch, onNext, onBack }) => {
  const [custom, setCustom] = useState("");
  const items = data.inventory;
  const qtyOf = (name) => items.find((i) => i.name === name)?.qty || 0;
  const setQty = (name, qty) => {
    const rest = items.filter((i) => i.name !== name);
    patch({ inventory: qty > 0 ? [...rest, { name, qty }] : rest, inventory_skipped: false });
  };
  const addCustom = () => {
    const name = custom.trim();
    if (!name) return;
    setQty(name, qtyOf(name) + 1);
    setCustom("");
  };
  const skip = () => { patch({ inventory: [], inventory_skipped: true }); onNext(); };
  const customItems = items.filter((i) => !INVENTORY_GROUPS.some((g) => g.items.includes(i.name)));

  return (
    <div data-testid="wz-step6">
      <StepTitle title="What are we moving?" sub="Tap + to add items and quantities — this helps us recommend the right truck." />
      <div className="space-y-6">
        {INVENTORY_GROUPS.map((g) => (
          <div key={g.room}>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">{g.room}</p>
            <div className="space-y-2">
              {g.items.map((name) => (
                <div key={name} className="flex items-center justify-between rounded-2xl border border-line bg-white px-4 py-2.5" data-testid={`wz-item-${name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <span className="text-sm font-semibold text-slate-700">{name}</span>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setQty(name, qtyOf(name) - 1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-slate-600 disabled:opacity-30" disabled={qtyOf(name) === 0} data-testid={`wz-item-${name.replace(/\s+/g, "-").toLowerCase()}-minus`}><Minus className="h-4 w-4" /></button>
                    <span className="w-5 text-center text-sm font-bold text-slate-900">{qtyOf(name)}</span>
                    <button type="button" onClick={() => setQty(name, qtyOf(name) + 1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-slate-600" data-testid={`wz-item-${name.replace(/\s+/g, "-").toLowerCase()}-plus`}><Plus className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {customItems.map((i) => (
          <div key={i.name} className="flex items-center justify-between rounded-2xl border border-blaze/40 bg-blaze/[0.04] px-4 py-2.5">
            <span className="text-sm font-semibold text-slate-700">{i.name}</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setQty(i.name, i.qty - 1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-slate-600"><Minus className="h-4 w-4" /></button>
              <span className="w-5 text-center text-sm font-bold">{i.qty}</span>
              <button type="button" onClick={() => setQty(i.name, i.qty + 1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-slate-600"><Plus className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <input className={wInput} placeholder="Add another item, e.g. Pool table" value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())} data-testid="wz-custom-item" />
          <button type="button" onClick={addCustom} className="flex shrink-0 items-center gap-1.5 rounded-2xl border-2 border-line px-4 text-xs font-bold uppercase tracking-widest text-slate-600 hover:border-blaze hover:text-blaze" data-testid="wz-custom-item-add"><PlusCircle className="h-4 w-4" /> Add</button>
        </div>
      </div>
      <NavRow onBack={onBack} onNext={onNext} testid="wz-step6-next" />
      <button type="button" onClick={skip} className="mt-3 w-full py-2 text-center text-xs font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-blaze" data-testid="wz-inventory-skip">
        I don't know / Skip inventory
      </button>
    </div>
  );
};

export const Step9Customer = ({ data, patch, onNext, onBack }) => {
  const [tried, setTried] = useState(false);
  const valid = data.first_name.trim() && data.last_name.trim() && data.mobile.trim() && /.+@.+\..+/.test(data.email);
  const go = () => (valid ? onNext() : setTried(true));
  return (
    <div data-testid="wz-step9">
      <StepTitle title="Almost done! Where should we send your quote?" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={wLabel}>First Name *</label><input className={wInput} value={data.first_name} onChange={(e) => patch({ first_name: e.target.value })} data-testid="wz-first-name" /></div>
        <div><label className={wLabel}>Last Name *</label><input className={wInput} value={data.last_name} onChange={(e) => patch({ last_name: e.target.value })} data-testid="wz-last-name" /></div>
        <div><label className={wLabel}>Mobile Number *</label><input type="tel" className={wInput} value={data.mobile} onChange={(e) => patch({ mobile: e.target.value })} data-testid="wz-mobile" /></div>
        <div><label className={wLabel}>Email Address *</label><input type="email" className={wInput} value={data.email} onChange={(e) => patch({ email: e.target.value })} data-testid="wz-email" /></div>
        <div className="sm:col-span-2">
          <label className={wLabel}>Additional Notes (optional)</label>
          <textarea rows={3} className={wInput} placeholder="Please tell us anything else we should know about your move." value={data.notes} onChange={(e) => patch({ notes: e.target.value })} data-testid="wz-notes" />
        </div>
      </div>
      {tried && !valid && <p className="mt-4 text-sm font-semibold text-blaze" data-testid="wz-step9-error">Please fill in your name, mobile and a valid email.</p>}
      <NavRow onBack={onBack} onNext={go} nextLabel="Review My Quote" testid="wz-step9-next" />
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-line/50 py-2.5 last:border-b-0">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</span>
    <span className="text-right text-sm font-semibold text-slate-800">{value || "—"}</span>
  </div>
);

export const Step10Summary = ({ data, estimate, estError, onRetryEstimate, settings, onBack, onSecure, onQuote, submitting }) => {
  const extras = (settings?.extra_services || []).filter((s) => data.extra_services.includes(s.id)).map((e) => e.label);
  if (data.boxes_qty > 0) extras.push(`Line Boxes × ${data.boxes_qty}`);
  if (data.stairs_qty > 0) extras.push(`Flights of Stairs × ${data.stairs_qty}`);
  const propLine = (p) => [p.type, p.floor, p.stairs === "Yes" ? `${p.stair_flights || 1} flight(s) of stairs` : ""].filter(Boolean).join(" · ");
  return (
    <div data-testid="wz-step10">
      <StepTitle title="Your quote summary" sub="Review everything before continuing." />
      <div className="rounded-2xl border border-line bg-white p-5" data-testid="wz-summary-move">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Your Move</p>
        <SummaryRow label="Pickup" value={data.pickup_suburb} />
        <SummaryRow label="Delivery" value={data.drop_suburb} />
        <SummaryRow label="Move Type" value={data.move_type} />
        <SummaryRow label="Property Size" value={data.property_size} />
        <SummaryRow label="Moving Date" value={data.moving_date} />
        <SummaryRow label="Preferred Time" value={data.start_time} />
        {data.pickup_prop?.type && <SummaryRow label="Pickup Property" value={propLine(data.pickup_prop)} />}
        {data.drop_prop?.type && <SummaryRow label="Delivery Property" value={propLine(data.drop_prop)} />}
        {data.inventory.length > 0 && <SummaryRow label="Items" value={data.inventory.map((i) => `${i.name} ×${i.qty}`).join(", ")} />}
        {extras.length > 0 && <SummaryRow label="Extra Services" value={extras.join(", ")} />}
      </div>

      <div className="mt-5 rounded-2xl border-2 border-blaze/30 bg-blaze/[0.04] p-5" data-testid="wz-summary-estimate">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Estimated Quote</p>
        {estError ? (
          <div className="py-4 text-center" data-testid="wz-estimate-error">
            <p className="text-sm font-semibold text-slate-700">We couldn't calculate your estimate right now.</p>
            <button type="button" onClick={onRetryEstimate} className="mt-3 rounded-xl border-2 border-blaze px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-blaze hover:bg-blaze hover:text-white" data-testid="wz-estimate-retry">Try Again</button>
          </div>
        ) : !estimate ? (
          <p className="py-4 text-center text-sm text-slate-500" data-testid="wz-estimate-loading">Calculating your estimate…</p>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-xl bg-white p-3">
              <Truck className="h-6 w-6 shrink-0 text-blaze" strokeWidth={1.8} />
              <div>
                <p className="text-sm font-bold text-slate-900" data-testid="wz-reco-truck">{estimate.truck.name} + {estimate.truck.movers} mover{estimate.truck.movers > 1 ? "s" : ""}</p>
                <p className="text-xs text-slate-500" data-testid="wz-reco-hours">Estimated moving time: {estimate.hours.billable} hrs{estimate.volume_m3 > 0 ? ` · ~${estimate.volume_m3} m³ of items` : ""}</p>
              </div>
            </div>
            <SummaryRow label="Truck + Movers" value={`$${estimate.costs.truck}`} />
            {estimate.costs.discount > 0 && <SummaryRow label="Small Truck Deal" value="10% off applied ✓" />}
            {(data.callout_hours || 0) > 0 ? (
              <SummaryRow label="Travel / Call-out" value={`+${data.callout_hours} ${data.callout_hours === 1 ? "hr" : "hrs"}`} />
            ) : (
              <SummaryRow label="Travel / Call-out" value={`$${estimate.costs.callout}`} />
            )}
            <SummaryRow label="Additional Services" value={estimate.costs.extras_total > 0 ? `$${estimate.costs.extras_total}` : "$0"} />
            {(data.callout_hours || 0) > 0 && (() => {
              const pk = data.callout_pickup_km || 0, dk = data.callout_drop_km || 0;
              const far = pk >= dk ? "pickup" : "drop-off";
              const reason = pk >= 80 && dk >= 80
                ? `both your pickup (${Math.round(pk)} km) and drop-off (${Math.round(dk)} km) are well outside Melbourne CBD`
                : (pk || dk) ? `your ${far} is ${Math.round(Math.max(pk, dk))} km from Melbourne CBD` : "your move is outside our standard Melbourne service area";
              return (
                <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-700" data-testid="wz-callout-note">
                  🚚 Because {reason}, a {data.callout_hours} {data.callout_hours === 1 ? "hr" : "hrs"} travel call-out is included in the job time.
                </p>
              );
            })()}
            <div className="mt-3 flex items-center justify-between border-t-2 border-blaze/30 pt-3">
              <span className="text-sm font-bold uppercase tracking-widest text-slate-900">Estimated Total</span>
              <span className="font-display text-2xl font-semibold text-blaze sm:text-3xl" data-testid="wz-estimate-total">${estimate.costs.total}</span>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-slate-400">Final price is confirmed when we call you. Stairs or heavy items over 100kg may incur an extra charge.</p>
          </>
        )}
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={onSecure}
          disabled={submitting || !estimate}
          className="w-full rounded-2xl bg-blaze py-4 text-sm font-bold uppercase tracking-widest text-white transition-all duration-200 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60"
          data-testid="wz-secure-booking"
        >
          {submitting ? "Please wait…" : "Continue to Secure My Booking"}
        </button>
        <button
          type="button"
          onClick={onQuote}
          disabled={submitting || !estimate}
          className="w-full rounded-2xl border-2 border-line bg-white py-4 text-sm font-bold uppercase tracking-widest text-slate-700 transition-colors hover:border-blaze hover:text-blaze disabled:opacity-60"
          data-testid="wz-request-quote"
        >
          Request Quote Only
        </button>
        {onBack && (
          <button type="button" onClick={onBack} className="py-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-slate-700" data-testid="wz-back">Back</button>
        )}
      </div>
    </div>
  );
};
